/**
 * DOM-free import parsers for GeoJSON / KML / GPX text.
 *
 * These run inside lib/geo.worker.js, where `DOMParser` does not exist, so KML and
 * GPX are read with a small tolerant XML tokenizer instead of the DOM. The output
 * matches what FileImportDialog's old DOMParser-based parsers produced.
 * Pure functions only: no window / document / localStorage.
 */

const ENTITIES = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };

function decodeEntities(s) {
  if (s.indexOf('&') === -1) return s;
  return s.replace(/&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g, (m, e) => {
    if (e[0] === '#') {
      const code = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
      try { return String.fromCodePoint(code); } catch { return m; }
    }
    return ENTITIES[e] ?? m;
  });
}

const localName = (n) => { const i = n.indexOf(':'); return i === -1 ? n : n.slice(i + 1); };

const TOKEN_RE = /<!--[\s\S]*?-->|<!\[CDATA\[([\s\S]*?)\]\]>|<\?[\s\S]*?\?>|<!DOCTYPE(?:[^>[]|\[[\s\S]*?\])*>|<\/([^\s>]+)\s*>|<([^\s/>!?]+)((?:\s+[^\s=/>]+\s*=\s*(?:"[^"]*"|'[^']*'))*)\s*(\/?)>/gi;
const ATTR_RE = /([^\s=/>]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g;

/**
 * Parse XML text into a light tree: { name, attrs, children: (node|string)[] }.
 * Element names are namespace-stripped. Never throws on malformed input; unmatched
 * close tags are ignored and unclosed elements are closed at end of input.
 */
export function parseXml(text, onProgress) {
  const root = { name: '#root', attrs: {}, children: [] };
  const stack = [root];
  let last = 0;
  let nextReport = 0;
  const total = text.length || 1;
  const top = () => stack[stack.length - 1];
  const addText = (s) => { if (s) top().children.push(s); };

  TOKEN_RE.lastIndex = 0;
  let m;
  while ((m = TOKEN_RE.exec(text)) !== null) {
    if (m.index > last) addText(decodeEntities(text.slice(last, m.index)));
    last = TOKEN_RE.lastIndex;
    if (m[1] !== undefined) {
      addText(m[1]); // CDATA is literal
    } else if (m[2] !== undefined) {
      const name = localName(m[2]);
      for (let i = stack.length - 1; i > 0; i--) {
        if (stack[i].name === name) { stack.length = i; break; }
      }
    } else if (m[3] !== undefined) {
      const attrs = {};
      if (m[4]) {
        ATTR_RE.lastIndex = 0;
        let a;
        while ((a = ATTR_RE.exec(m[4])) !== null) attrs[localName(a[1])] = decodeEntities(a[2] ?? a[3] ?? '');
      }
      const node = { name: localName(m[3]), attrs, children: [] };
      top().children.push(node);
      if (!m[5]) stack.push(node);
    }
    if (onProgress && last >= nextReport) {
      onProgress(last / total);
      nextReport = last + total / 20;
    }
  }
  if (last < text.length) addText(decodeEntities(text.slice(last)));
  return root;
}

const isEl = (c) => typeof c !== 'string';

/** All descendant elements called `name`, in document order. */
export function descendants(node, name, out = []) {
  for (const c of node.children) {
    if (!isEl(c)) continue;
    if (c.name === name) out.push(c);
    descendants(c, name, out);
  }
  return out;
}

/** First descendant matching the nested path (CSS descendant-combinator semantics). */
export function queryPath(node, path) {
  const [head, ...rest] = path;
  for (const c of node.children) {
    if (!isEl(c)) continue;
    if (c.name === head) {
      if (rest.length === 0) return c;
      const hit = queryPath(c, rest);
      if (hit) return hit;
    }
    const deep = queryPath(c, path);
    if (deep) return deep;
  }
  return null;
}

export function textContent(node) {
  if (!node) return '';
  return node.children.map(c => (isEl(c) ? textContent(c) : c)).join('');
}

function parseKMLCoords(text) {
  return text.trim().split(/\s+/).map(coord => {
    const [lon, lat] = coord.split(',').map(Number);
    return [lon, lat];
  }).filter(c => !isNaN(c[0]) && !isNaN(c[1]));
}

const featureCollection = (features) => ({ type: 'FeatureCollection', features });

/** KML text -> FeatureCollection (Point, LineString, Polygon outer ring). */
export function parseKMLText(kml, onProgress) {
  const doc = parseXml(kml, onProgress && ((p) => onProgress(p * 0.5)));
  const placemarks = descendants(doc, 'Placemark');
  const features = [];
  placemarks.forEach((pm, i) => {
    const name = textContent(queryPath(pm, ['name']));
    const description = textContent(queryPath(pm, ['description']));
    const properties = { name, description };

    const point = queryPath(pm, ['Point', 'coordinates']);
    if (point) {
      const [lon, lat] = textContent(point).trim().split(',').map(Number);
      features.push({ type: 'Feature', properties, geometry: { type: 'Point', coordinates: [lon, lat] } });
    } else {
      const line = queryPath(pm, ['LineString', 'coordinates']);
      if (line) {
        features.push({ type: 'Feature', properties, geometry: { type: 'LineString', coordinates: parseKMLCoords(textContent(line)) } });
      } else {
        const poly = queryPath(pm, ['Polygon', 'outerBoundaryIs', 'LinearRing', 'coordinates']);
        if (poly) {
          features.push({ type: 'Feature', properties, geometry: { type: 'Polygon', coordinates: [parseKMLCoords(textContent(poly))] } });
        }
      }
    }
    if (onProgress && (i & 255) === 0) onProgress(0.5 + 0.5 * (i / placemarks.length));
  });
  return featureCollection(features);
}

function gpxPoint(pt) {
  const lat = parseFloat(pt.attrs.lat);
  const lon = parseFloat(pt.attrs.lon);
  return isNaN(lat) || isNaN(lon) ? null : [lon, lat];
}

/** GPX text -> FeatureCollection (waypoints, track segments, routes). */
export function parseGPXText(gpx, onProgress) {
  const doc = parseXml(gpx, onProgress && ((p) => onProgress(p * 0.5)));
  const features = [];
  const nameOf = (n) => textContent(queryPath(n, ['name']));

  descendants(doc, 'wpt').forEach(wpt => {
    const c = gpxPoint(wpt);
    if (c) features.push({ type: 'Feature', properties: { name: nameOf(wpt) }, geometry: { type: 'Point', coordinates: c } });
  });

  const tracks = descendants(doc, 'trk');
  tracks.forEach((trk, i) => {
    const name = nameOf(trk);
    descendants(trk, 'trkseg').forEach(seg => {
      const coords = descendants(seg, 'trkpt').map(gpxPoint).filter(Boolean);
      if (coords.length >= 2) features.push({ type: 'Feature', properties: { name }, geometry: { type: 'LineString', coordinates: coords } });
    });
    if (onProgress) onProgress(0.5 + 0.5 * ((i + 1) / tracks.length));
  });

  descendants(doc, 'rte').forEach(rte => {
    const coords = descendants(rte, 'rtept').map(gpxPoint).filter(Boolean);
    if (coords.length >= 2) features.push({ type: 'Feature', properties: { name: nameOf(rte) }, geometry: { type: 'LineString', coordinates: coords } });
  });
  return featureCollection(features);
}

const GEOMETRY_TYPES = ['Point', 'MultiPoint', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon', 'GeometryCollection'];

/**
 * A readable message for a JSON syntax error: "This file is not valid GeoJSON (line 7)."
 * The line comes from the engine's "line N" or "position N" text; the raw parser message
 * is never shown (V.49).
 */
export function friendlyJsonError(text, err) {
  if (!String(text || '').trim()) return 'This file is empty: it is not valid GeoJSON.';
  const msg = String(err?.message || '');
  let line = null;
  const lm = /line (\d+)/i.exec(msg);
  const pm = /position (\d+)/i.exec(msg);
  if (lm) line = Number(lm[1]);
  else if (pm) line = String(text).slice(0, Number(pm[1])).split('\n').length;
  return `This file is not valid GeoJSON${line ? ` (line ${line})` : ''}. Check for a missing comma, bracket or quote near that line.`;
}

/** GeoJSON text -> FeatureCollection (a bare Feature or geometry is wrapped). */
export function parseGeoJSONText(text) {
  let geojson;
  try {
    geojson = JSON.parse(text);
  } catch (e) {
    throw new Error(friendlyJsonError(text, e));
  }
  if (geojson && GEOMETRY_TYPES.includes(geojson.type)) geojson = { type: 'Feature', properties: {}, geometry: geojson };
  if (geojson && geojson.type === 'Feature') geojson = featureCollection([geojson]);
  if (!geojson || geojson.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
    throw new Error('This file is JSON but not GeoJSON: expected a FeatureCollection, Feature or geometry.');
  }
  return geojson;
}

/**
 * Dispatch by file extension. `format` is one of geojson | json | kml | gpx.
 * @param {(fraction:number, message?:string)=>void} [onProgress]
 */
export function parseGeoText(format, text, onProgress) {
  switch (format) {
    case 'geojson':
    case 'json': { onProgress?.(0.1, 'Parsing JSON'); return parseGeoJSONText(text); }
    case 'kml': return parseKMLText(text, onProgress && ((p) => onProgress(p, 'Parsing KML')));
    case 'gpx': return parseGPXText(text, onProgress && ((p) => onProgress(p, 'Parsing GPX')));
    default: throw new Error(`Unsupported import format: ${format}`);
  }
}
